import { collection, documentId, getDocs, limit, onSnapshot, orderBy, query, startAfter, where, type QueryDocumentSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getFirebaseServices } from "./firebase";
import type { Listing, ModuleKey } from "./terra-boa-data";
import type { TerraBoaSession } from "./terra-boa-repository";
import type { DistributorCampaign, DistributorSponsor } from "./terra-boa-distributors";

export function subscribeDistributorSponsors(onData: (items: DistributorSponsor[]) => void, onError: () => void, admin = false) {
  const services = getFirebaseServices();
  if (!services) { onData([]); return () => undefined; }
  const source = collection(services.db, "distributorSponsors");
  return onSnapshot(admin ? source : query(source, where("active", "==", true)), snapshot => onData(snapshot.docs.map(item => item.data() as DistributorSponsor)), onError);
}
export function subscribeDistributorCampaigns(onData: (items: DistributorCampaign[]) => void, onError: () => void, ownerUid?: string, admin = false, scope?: { module?: ModuleKey; profileId?: string }) {
  const services = getFirebaseServices();
  if (!services) { onData([]); return () => undefined; }
  const source = collection(services.db, "distributorCampaigns");
  const scoped = admin ? source : ownerUid ? query(source, where("ownerUid", "==", ownerUid)) : query(source, where("active", "==", true),
    ...(scope?.module ? [where("module", "==", scope.module)] : []), ...(scope?.profileId ? [where("profileId", "==", scope.profileId)] : []));
  return onSnapshot(scoped, snapshot => onData(snapshot.docs.map(item => item.data() as DistributorCampaign)), onError);
}
export async function configureDistributorSponsor(session: TerraBoaSession, sponsor: DistributorSponsor) {
  if (!session.uid || !session.isAdmin) throw new Error("Acesso administrativo necessário.");
  await httpsCallable(getFirebaseServices()!.functions, "configureTerraBoaDistributorSponsor")({ sponsor });
}
export async function saveDistributorCampaign(session: TerraBoaSession, campaign: DistributorCampaign) {
  if (!session.uid) throw new Error("Entre para continuar.");
  await httpsCallable(getFirebaseServices()!.functions, "saveTerraBoaDistributorCampaign")({ campaign });
}
export async function moderateDistributorCampaign(session: TerraBoaSession, id: string, active: boolean, remove = false) {
  if (!session.uid || !session.isAdmin) throw new Error("Acesso administrativo necessário.");
  await httpsCallable(getFirebaseServices()!.functions, "moderateTerraBoaDistributorCampaign")({ id, active, remove });
}
export async function deleteDistributorCampaign(session: TerraBoaSession, id: string) {
  if (!session.uid) throw new Error("Entre para continuar.");
  await httpsCallable(getFirebaseServices()!.functions, "deleteTerraBoaDistributorCampaign")({ id });
}
export async function distributorListingPage(ownerId: string, module: ModuleKey | "sales", cursor?: QueryDocumentSnapshot) {
  const services = getFirebaseServices();
  if (!services) return { rows: [] as Listing[], cursor: undefined, more: false };
  const source = query(collection(services.db, "listings"), where("ownerId", "==", ownerId),
    ...(module === "sales" ? [where("module", "in", ["hortifruti", "graos", "pecuaria"])] : [where("module", "==", module)]),
    orderBy(documentId(), "desc"), ...(cursor ? [startAfter(cursor)] : []), limit(24));
  const snapshot = await getDocs(source);
  return { rows: snapshot.docs.map(item => item.data() as Listing), cursor: snapshot.docs.at(-1), more: snapshot.size === 24 };
}
